import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomizedSwitch, StatusCard, ToolInfo } from 'components';
import {
    goalDataTypeEnums,
    Images,
    evalModeEnums,
    masteryEvalTypeEnums,
    masteryDirectionEnums,
    renderTargetStatuses,
    targetStatusEnums,
    PermissionList,
    RolePermission,
} from 'utils';
import { goalActions } from 'store';
import { FirstTrialSwitch, toggleFirstTrial } from './firstTrialCriteria';
import { BASELINE_FIELDS, CriteriaRuleRow, CriteriaSaveBar, isRuleValid, useCriteriaDraft } from './criteriaForm';

const CHANGE_BASELINE_CRITERIA = 'CHANGE_BASELINE_CRITERIA';

/**
 * Goal-level BASELINE -> TEACHING criteria (default for all targets).
 * Mirrors the Target Mastery box but drives the baseline phase.
 */
export const BaselineCriteriaOption = ({}) => {
    const goalById = useSelector((state) => state.goals.goalById);
    const dispatch = useDispatch();
    const hasPermission = RolePermission([PermissionList.GOAL_CREATE?.code]);
    const baselineCriteria = useCriteriaDraft({ fields: BASELINE_FIELDS, actionType: CHANGE_BASELINE_CRITERIA });

    useEffect(() => {
        if (goalById?.baselineCriteria?.evalMode) {
            baselineCriteria.sync(goalById?.baselineCriteria);
        } else if (goalById?.id) {
            // Legacy goal created before baselineCriteria existed — seed sensible
            // defaults from targetMastery so the card renders; the first save persists it.
            // Auto Transition stays OFF by default (clinician opts in).
            const tm = goalById?.targetMastery || {};
            baselineCriteria.sync({
                autoTransition: false,
                direction: tm.direction || masteryDirectionEnums.ABOVE,
                evalMode: tm.evalMode || evalModeEnums.BY_SESSION,
                evalType: masteryEvalTypeEnums.CONSECUTIVE,
                evalModeCount: tm.evalModeCount || 5,
                count: tm.count || (goalById?.dataType === goalDataTypeEnums?.PERCENT ? 90 : 1),
            });
        }
    }, [goalById]);

    if (!goalById?.id) {
        return null;
    }

    return (
        <div className="mastery-box">
            <p className="mastery-title">Baseline Criteria</p>
            <div className="mastery-info-box">
                <div className="mastery-status-row">
                    <div className="space-between-align-center" style={{ padding: 12 }}>
                        <div className="flex-align-center" style={{ gap: 4 }}>
                            <StatusCard
                                status={renderTargetStatuses?.find((j) => targetStatusEnums.BASELINE === j?.value)?.label}
                                custom={renderTargetStatuses?.find((j) => targetStatusEnums.BASELINE === j?.value)}
                            />
                            <img src={Images.arrowRight} alt="arrow" />
                            <StatusCard
                                status={renderTargetStatuses?.find((j) => targetStatusEnums.TEACHING === j?.value)?.label}
                                custom={renderTargetStatuses?.find((j) => targetStatusEnums.TEACHING === j?.value)}
                            />
                        </div>
                        <div className="flex-align-center" style={{ gap: 16 }}>
                            <FirstTrialSwitch
                                goal={goalById}
                                disabled={!hasPermission}
                                checked={baselineCriteria.draft?.firstTrialOnly}
                                onToggle={() => baselineCriteria.update(toggleFirstTrial(baselineCriteria.draft))}
                            />
                            <div className="switcher-style" style={{ gap: 16 }}>
                                <p style={{ width: 'auto', padding: 0 }}>Auto Transition</p>
                                <CustomizedSwitch
                                    disabled={!hasPermission}
                                    checked={baselineCriteria.draft?.autoTransition}
                                    handleClick={() => baselineCriteria.update({ autoTransition: !baselineCriteria.draft?.autoTransition })}
                                />
                            </div>
                            <ToolInfo
                                text={
                                    'Automatically move targets from baseline to teaching based on the selected ' +
                                    'sessions or days — consecutive, or on average.'
                                }
                            />
                        </div>
                    </div>
                    <div style={{ borderTop: '1px solid #EBF2FD', padding: 12 }}>
                        <CriteriaRuleRow
                            goal={goalById}
                            subject="Move to teaching"
                            criteria={baselineCriteria.draft}
                            disabled={!hasPermission}
                            onChange={baselineCriteria.update}
                        />
                    </div>
                    <CriteriaSaveBar
                        dirty={baselineCriteria.isDirty}
                        saving={baselineCriteria.isSaving}
                        disabled={!isRuleValid(baselineCriteria.draft)}
                        onCancel={baselineCriteria.reset}
                        onSave={() => baselineCriteria.save((body) => dispatch(goalActions.changeBaselineCriteria(goalById?.id, body)))}
                    />
                </div>
            </div>
        </div>
    );
};
